import React, { useMemo, useState } from 'react';
import { CheckCircle, RefreshCw, WifiOff } from 'lucide-react';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import EmptyState from '../EmptyState';
import { useSeating, type UseSeating } from '../../hooks/useSeating';
import { filterGuests } from '../../hooks/useDoor';
import { useHeaderSearch } from '../../hooks/useHeaderSearch';
import type { SeatingApi } from '../../services/SeatingApiClient';
import type { DoorGuest } from '../../types/Door';
import type { SeatingTable } from '../../types/Seating';
import { fillOf, groupByTable, tableLabel, tableMatches, tableOptionLabel } from '../../utils/seating';
import '../../styles/components/SeatingScreen.css';

export interface SeatingEventOption {
  id: string;
  title: string;
}

interface SeatingScreenProps {
  api: SeatingApi;
  eventId: string | null;
  events: readonly SeatingEventOption[];
  onEventChange?: (eventId: string) => void;
  /** Planner or above. Below that the screen only shows the seating. The server enforces it too. */
  canEdit: boolean;
  /** 0 turns the background refresh off. */
  pollMs?: number;
}

type View = 'guests' | 'tables';

const norm = (s: string): string => s.toLowerCase();

function guestMatches(g: DoorGuest, q: string): boolean {
  if (!q.trim()) return true;
  if (filterGuests([g], q, 'all').length > 0) return true;
  return g.table != null && norm(tableLabel(g.table)).includes(norm(q.trim()));
}

function GuestBadges({ g }: { g: DoorGuest }) {
  return (
    <div className="seating-badges">
      {g.checked_in && <span className="seating-badge seating-badge--in"><CheckCircle size={12} aria-hidden /> Arrived</span>}
      {g.walk_in && <span className="seating-badge">Walk-in</span>}
      {g.rsvp_status === 'declined' && <span className="seating-badge seating-badge--warn">Declined</span>}
      {g.plus_ones.length > 0 && <span className="seating-badge">+{g.plus_ones.length}</span>}
      {g.tags.map((t) => <span key={t} className="seating-badge seating-badge--tag">{t}</span>)}
    </div>
  );
}

function FullNotice({ guestName, seating, canEdit }: { guestName: string; seating: UseSeating; canEdit: boolean }) {
  const r = seating.fullRefusal;
  if (!r) return null;
  const fill = fillOf(r.table, r.seated);
  return (
    <div className="seating-full" role="alert">
      <p className="seating-full__text">
        {tableLabel(r.table)} is full ({r.seated} of {fill.capacity} seated). {guestName} was not moved.
        {canEdit ? ' A planner can seat them there anyway.' : ''}
      </p>
      <div className="seating-full__actions">
        {canEdit && <Button variant="primary" size="md" loading={seating.busyId === r.guestId} onClick={() => { void seating.seatAnyway(); }}>Seat anyway</Button>}
        <Button variant="secondary" size="md" onClick={seating.dismissFull}>Cancel</Button>
      </div>
    </div>
  );
}

function GuestRow({ g, seating, canEdit }: { g: DoorGuest; seating: UseSeating; canEdit: boolean }) {
  const busy = seating.busyId === g.id;
  const current = g.table?.id ?? '';
  const usable = seating.tables.some((t) => t.id === current);
  const [seatDeclined, setSeatDeclined] = useState(false);
  const declined = g.rsvp_status === 'declined' && !g.table;
  const holdBack = declined && !seatDeclined;
  return (
    <li className="seating-row">
      <div className="seating-row__main">
        <div className="seating-row__name">{g.name}</div>
        <GuestBadges g={g} />
      </div>
      <div className="seating-row__seat">
        {holdBack ? (
          <>
            <span className="seating-row__table">Declined, no table</span>
            {canEdit && <Button variant="secondary" size="md" onClick={() => setSeatDeclined(true)}>Seat anyway</Button>}
          </>
        ) : canEdit && (seating.tables.length > 0 || g.table) ? (
          <select
            className="form-select seating-select"
            aria-label={`Table for ${g.name}`}
            value={current}
            disabled={busy}
            onChange={(e) => { void seating.assign(g.id, e.target.value === '' ? null : e.target.value, declined ? { allowDeclined: true } : undefined); }}
          >
            <option value="">No table</option>
            {g.table && !usable && <option value={g.table.id}>{tableLabel(g.table)} (not used at this event)</option>}
            {seating.tables.map((t) => <option key={t.id} value={t.id}>{tableOptionLabel(t, seating.seated.get(t.id) ?? 0)}</option>)}
          </select>
        ) : (
          <span className="seating-row__table">{g.table ? tableLabel(g.table) : 'No table'}</span>
        )}
      </div>
      {seating.fullRefusal?.guestId === g.id && <FullNotice guestName={g.name} seating={seating} canEdit={canEdit} />}
    </li>
  );
}

function TableCard({ table, guests, waiterName, q }: { table: SeatingTable; guests: DoorGuest[]; waiterName: string | null; q: string }) {
  const fill = fillOf(table, guests.length);
  const label = tableLabel(table);
  const summary = fill.capacity == null ? `${guests.length} seated` : `${guests.length} of ${fill.capacity} seated`;
  return (
    <article className={`seating-table${fill.over > 0 ? ' seating-table--over' : ''}`} aria-label={label}>
      <header className="seating-table__head">
        <h3 className="heading-card seating-table__title">{label}</h3>
        <span className="seating-table__count">{summary}{fill.over > 0 ? `, ${fill.over} over` : ''}</span>
      </header>
      <p className="seating-table__meta">
        {table.section ? `${table.section}. ` : ''}{waiterName ? `Waiter: ${waiterName}` : 'No waiter'}
      </p>
      {guests.length === 0 ? (
        <p className="seating-table__empty">No guests seated</p>
      ) : (
        <ul className="seating-table__guests">
          {guests.map((g) => (
            <li key={g.id} className={`seating-table__guest${q.trim() && guestMatches(g, q) ? ' seating-table__guest--match' : ''}`}>
              <span className="seating-table__guest-name">{g.name}</span>
              <GuestBadges g={g} />
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

function GuestGroup({ title, guests }: { title: string; guests: DoorGuest[] }) {
  if (guests.length === 0) return null;
  return (
    <article className="seating-table seating-table--loose" aria-label={title}>
      <header className="seating-table__head">
        <h3 className="heading-card seating-table__title">{title}</h3>
        <span className="seating-table__count">{guests.length}</span>
      </header>
      <ul className="seating-table__guests">
        {guests.map((g) => (
          <li key={g.id} className="seating-table__guest">
            <span className="seating-table__guest-name">{g.name}</span>
            <GuestBadges g={g} />
          </li>
        ))}
      </ul>
    </article>
  );
}

/**
 * Seating for one event: who sits where. "By guest" assigns or clears a guest's table; "By table" lists each table with its
 * guests and waiter. Search is the global search in the header (no box here). Below planner it is read only.
 */
const SeatingScreen: React.FC<SeatingScreenProps> = ({ api, eventId, events, onEventChange, canEdit, pollMs }) => {
  const seating = useSeating(api, eventId, pollMs);
  const q = useHeaderSearch();
  const [view, setView] = useState<View>('guests');

  const waiterName = useMemo(() => {
    const byId = new Map(seating.waiters.map((w) => [w.id, w.name]));
    return (t: SeatingTable) => (t.waiter_id ? byId.get(t.waiter_id) ?? null : null);
  }, [seating.waiters]);
  const visibleGuests = useMemo(() => seating.guests.filter((g) => guestMatches(g, q)), [seating.guests, q]);
  const grouped = useMemo(() => groupByTable(seating.tables, seating.guests), [seating.tables, seating.guests]);
  const visibleGroups = useMemo(
    () => grouped.groups.filter((gr) => tableMatches(gr.table, waiterName(gr.table), q) || gr.guests.some((g) => guestMatches(g, q))),
    [grouped.groups, q, waiterName],
  );

  const picker = (
    <div className="seating-toolbar">
      <label className="seating-field">
        <span className="seating-field__label">Event</span>
        <select
          className="form-select seating-select"
          aria-label="Event"
          value={eventId ?? ''}
          onChange={(e) => { if (e.target.value) onEventChange?.(e.target.value); }}
        >
          {!eventId && <option value="">Choose an event</option>}
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
        </select>
      </label>
    </div>
  );

  if (!eventId) {
    return (
      <div className="seating fade-in">
        {picker}
        <EmptyState icon="calendar" title="Choose an event" description="Seating is per event. Pick the event above to see who sits where." />
      </div>
    );
  }

  let body: React.ReactNode;
  if (seating.status === 'loading') {
    body = (
      <div className="seating-state" role="status" aria-live="polite">
        <RefreshCw size={24} className="seating-spin" aria-hidden />
        <p>Loading the seating…</p>
      </div>
    );
  } else if (seating.status === 'ended') {
    body = (
      <div className="seating-state" role="alert">
        <h3>This session has ended</h3>
        <p>Your sign-in may have expired. Sign in again to see the seating.</p>
      </div>
    );
  } else if (seating.status === 'missing') {
    body = (
      <div className="seating-state" role="alert">
        <h3>Event not found</h3>
        <p>This event is not available to this login. Choose another event above.</p>
      </div>
    );
  } else if (seating.status === 'unavailable') {
    body = <LoadErrorBox what="the seating" error={seating.error} onRetry={() => { void seating.refresh(); }} />;
  } else {
    const seatedCount = seating.guests.filter((g) => g.table).length;
    body = (
      <>
        {seating.stale && (
          <div className="seating-banner" role="status">
            <WifiOff size={16} aria-hidden /> Not connected. Showing the seating as it was a moment ago; it updates when the connection returns.
          </div>
        )}
        <p className="seating-summary" role="status">
          {seating.guests.length} {seating.guests.length === 1 ? 'guest' : 'guests'}, {seatedCount} seated, {seating.guests.length - seatedCount} not seated.
          {!canEdit && ' Read only: a planner changes the seating.'}
        </p>
        {seating.actionError && (
          <div className="seating-full" role="alert">
            <p className="seating-full__text">{seating.actionError}</p>
            <div className="seating-full__actions"><Button variant="secondary" size="md" onClick={seating.clearActionError}>Dismiss</Button></div>
          </div>
        )}
        <div className="seating-views" role="group" aria-label="View">
          <button type="button" className="seating-chip" aria-pressed={view === 'guests'} onClick={() => setView('guests')}>By guest</button>
          <button type="button" className="seating-chip" aria-pressed={view === 'tables'} onClick={() => setView('tables')}>By table</button>
        </div>
        {seating.tables.length === 0 && (
          <EmptyState compact title="No tables for this event yet" description="Tables are set up by a planner. Guests can be seated once there are tables." />
        )}
        {view === 'guests' ? (
          seating.guests.length === 0 ? (
            <EmptyState compact title="No guests on this event yet" description="Guests invited to this event appear here." />
          ) : visibleGuests.length === 0 ? (
            <EmptyState compact title="No one matches" description="Clear the search at the top of the page to see everyone." />
          ) : (
            <ul className="seating-list" aria-label="Guests">
              {visibleGuests.map((g) => <GuestRow key={g.id} g={g} seating={seating} canEdit={canEdit} />)}
            </ul>
          )
        ) : (
          <div className="seating-tables">
            {visibleGroups.map((gr) => <TableCard key={gr.table.id} table={gr.table} guests={gr.guests} waiterName={waiterName(gr.table)} q={q} />)}
            <GuestGroup title="Not seated yet" guests={grouped.unseated.filter((g) => guestMatches(g, q))} />
            <GuestGroup title="At a table not used for this event" guests={grouped.unplaced.filter((g) => guestMatches(g, q))} />
            {visibleGroups.length === 0 && seating.tables.length > 0 && q.trim() !== '' && (
              <EmptyState compact title="No table matches" description="Clear the search at the top of the page to see every table." />
            )}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="seating fade-in">
      {picker}
      {body}
    </div>
  );
};

export default SeatingScreen;
